import {gql} from '@apollo/client';
import {groupBy} from '@benchling/js-utils';
import {useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getBioentityTargetFileIdsFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import type {UnitSummary} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {getEquipmentModelId} from 'procedures/shared-components/validation/getEquipmentModelId';
import useBioentitiesForProcedures from 'procedures/utils/useBioentitiesForProcedures';
import type {BioentityForProcedure} from 'procedures/utils/useBioentitiesForProcedures';

import type {
  PMEISidepanelDetailQuery,
  PMEISidepanelDetailQuery_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode as PMEINode,
  PMEISidepanelDetailQuery_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode_templateMethodVersion_ProcedureMethodDefinitionVersionNode as TemplateMethodVersionNode,
  PMEISidepanelDetailQueryVariables,
} from './__generated/PMEISidepanelDetailQuery';
import {buildDownstreamIndex, buildOriginIndex} from './PMEIMaterialLinkUtils';
import type {FlowchartVersionData, LinkedPMEI} from './PMEIMaterialLinkUtils';
import type {UnitOperationFields} from './useUnitOpLinkingState';

export const PMEI_SIDEPANEL_DETAIL_QUERY = gql`
  query PMEISidepanelDetailQuery($pmeiId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureMethodExecutionInstance(id: $pmeiId) {
      ...ProcedureMethodExecutionInstancePlannedValuesGraphQL
      ... on ProcedureMethodExecutionInstanceNode {
        id
        worksheet {
          ... on WorksheetNode {
            id
            name
            displayId
          }
          ...InaccessibleInfoGraphQL
        }
        templateMethodVersion {
          ... on ProcedureMethodDefinitionVersionNode {
            id
            name
            dataParameterSettings {
              version {
                id
                name
                dataType
                minimum
                maximum
              }
            }
            materialInputSettings {
              version {
                id
                name
                dataType
                amountUnit
              }
            }
            materialOutputSettings {
              version {
                id
                name
                dataType
              }
            }
            equipmentSettings {
              version {
                id
                name
                dataType
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        dataParameterConfirmationValues {
          id
          value
          parameterVersion {
            id
          }
        }
        dataParameterActualValues {
          id
          value
          parameterVersion {
            id
          }
        }
        materialInputConfirmationValues {
          id
          material
          amount
          parameterVersion {
            id
          }
        }
        materialOutputConfirmationValues {
          id
          material
          materialOutputVersion {
            id
            name
          }
        }
        equipmentConfirmationValues {
          id
          equipment
          parameterVersion {
            id
          }
        }
        nextProcedureMethodExecutionInstances {
          ... on ProcedureMethodExecutionInstanceNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
        previousProcedureMethodExecutionInstances {
          ... on ProcedureMethodExecutionInstanceNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcedureMethodExecutionInstancePlannedValues').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface PMEISidepanelDetailData {
  worksheetId: string | null;
  worksheetDisplayId: string | null;
  parameterData: PMEIParameterData | null;
  downstreamIndex: Map<string, Array<LinkedPMEI>> | null;
  originIndex: Map<string, Array<LinkedPMEI>> | null;
}

interface DataParameterValueRecord {
  id: string;
  value: TypedValue;
}
interface MaterialRecord {
  id: string;
  material: TypedValue;
}
type MaterialInputValueRecord = MaterialRecord & {amount: TypedValue | null};
interface EquipmentRecord {
  id: string;
  equipment: TypedValue;
  /** Id of the entity linked by the equipment entity's "Model" field; null for text equipment. */
  modelId: string | null;
}

export interface PMEISidepanelDataParameterRow {
  version: TemplateMethodVersionNode['dataParameterSettings'][number]['version'];
  plannedValues: Array<DataParameterValueRecord>;
  confirmationValues: Array<DataParameterValueRecord>;
  actualValues: Array<DataParameterValueRecord>;
}

export interface PMEISidepanelMaterialInputRow {
  version: {
    id: string;
    name: string;
    dataType: TemplateMethodVersionNode['materialInputSettings'][number]['version']['dataType'];
    amountUnit: UnitSummary | null;
  };
  plannedValues: Array<MaterialInputValueRecord>;
  confirmationValues: Array<MaterialInputValueRecord>;
}

export interface PMEISidepanelMaterialOutputRow {
  version: TemplateMethodVersionNode['materialOutputSettings'][number]['version'];
  confirmationValues: Array<MaterialRecord>;
}

export interface PMEISidepanelEquipmentRow {
  version: TemplateMethodVersionNode['equipmentSettings'][number]['version'];
  plannedValues: Array<EquipmentRecord>;
  confirmationValues: Array<EquipmentRecord>;
}

export interface PMEIParameterData {
  dataParameters: Array<PMEISidepanelDataParameterRow>;
  materialInputs: Array<PMEISidepanelMaterialInputRow>;
  materialOutputs: Array<PMEISidepanelMaterialOutputRow>;
  equipment: Array<PMEISidepanelEquipmentRow>;
}

function extractParameterData(
  pmei: PMEINode,
  bioentitiesById: Map<string, BioentityForProcedure>
): PMEIParameterData | null {
  const tmv = pmei.templateMethodVersion;
  if (tmv == null || isInaccessible(tmv)) {
    return null;
  }

  const dpPlanned = groupBy(pmei.dataParameterPlannedValues, (v) => v.parameterVersion.id);
  const dpConfirmed = groupBy(pmei.dataParameterConfirmationValues, (v) => v.parameterVersion.id);
  const dpActual = groupBy(pmei.dataParameterActualValues, (v) => v.parameterVersion.id);
  const miPlanned = groupBy(pmei.materialInputPlannedValues, (v) => v.parameterVersion.id);
  const miConfirmed = groupBy(pmei.materialInputConfirmationValues, (v) => v.parameterVersion.id);
  const moConfirmed = groupBy(pmei.materialOutputConfirmationValues, (v) => v.materialOutputVersion.id);
  const eqPlanned = groupBy(pmei.equipmentPlannedValues, (v) => v.parameterVersion.id);
  const eqConfirmed = groupBy(pmei.equipmentConfirmationValues, (v) => v.parameterVersion.id);

  return {
    dataParameters: tmv.dataParameterSettings.map((s) => ({
      version: s.version,
      plannedValues: (dpPlanned[s.version.id] ?? []).map((v) => ({id: v.id, value: v.value as TypedValue})),
      confirmationValues: (dpConfirmed[s.version.id] ?? []).map((v) => ({
        id: v.id,
        value: v.value as TypedValue,
      })),
      actualValues: (dpActual[s.version.id] ?? []).map((v) => ({id: v.id, value: v.value as TypedValue})),
    })),
    materialInputs: tmv.materialInputSettings.map((s) => ({
      version: {
        id: s.version.id,
        name: s.version.name,
        dataType: s.version.dataType,
        amountUnit: s.version.amountUnit as UnitSummary | null,
      },
      plannedValues: (miPlanned[s.version.id] ?? []).map((v) => ({
        id: v.id,
        material: v.material as TypedValue,
        amount: v.amount as TypedValue | null,
      })),
      confirmationValues: (miConfirmed[s.version.id] ?? []).map((v) => ({
        id: v.id,
        material: v.material as TypedValue,
        amount: v.amount as TypedValue,
      })),
    })),
    materialOutputs: tmv.materialOutputSettings.map((s) => ({
      version: s.version,
      confirmationValues: (moConfirmed[s.version.id] ?? []).map((v) => ({
        id: v.id,
        material: v.material as TypedValue,
      })),
    })),
    equipment: tmv.equipmentSettings.map((s) => ({
      version: s.version,
      plannedValues: (eqPlanned[s.version.id] ?? []).map((v) =>
        toEquipmentRecord(v.id, v.equipment as TypedValue, bioentitiesById)
      ),
      confirmationValues: (eqConfirmed[s.version.id] ?? []).map((v) =>
        toEquipmentRecord(v.id, v.equipment as TypedValue, bioentitiesById)
      ),
    })),
  };
}

function toEquipmentRecord(
  id: string,
  equipment: TypedValue,
  bioentitiesById: Map<string, BioentityForProcedure>
): EquipmentRecord {
  return {id, equipment, modelId: getEquipmentModelId(equipment, bioentitiesById)};
}

/** Ids of the entities linked by every equipment value on the PMEI (planned and confirmed). */
function equipmentEntityIds(pmei: PMEINode): Array<string> {
  const equipmentValues = [
    ...pmei.equipmentPlannedValues.map((v) => v.equipment as TypedValue),
    ...pmei.equipmentConfirmationValues.map((v) => v.equipment as TypedValue),
  ];
  return equipmentValues.flatMap(
    (value) => getBioentityTargetFileIdsFromTypedValue(value)?.filter((id): id is string => id != null) ?? []
  );
}

function buildPMEISidepanelDetailData(
  pmei: PMEISidepanelDetailQuery['procedureMethodExecutionInstance'],
  flowchartData: FlowchartVersionData | null,
  pmeiIdToUnitOperationFields: Map<string, UnitOperationFields>,
  bioentitiesById: Map<string, BioentityForProcedure>
): PMEISidepanelDetailData | null {
  if (isInaccessible(pmei)) {
    return null;
  }

  const worksheet = pmei.worksheet;
  const hasWorksheet = !isInaccessible(worksheet);

  return {
    worksheetId: hasWorksheet ? worksheet.id : null,
    worksheetDisplayId: hasWorksheet ? (worksheet.displayId ?? worksheet.name) : null,
    parameterData: extractParameterData(pmei, bioentitiesById),
    downstreamIndex: extractMaterialLinkIndex(
      pmei.nextProcedureMethodExecutionInstances,
      flowchartData,
      pmeiIdToUnitOperationFields,
      buildDownstreamIndex
    ),
    originIndex: extractMaterialLinkIndex(
      pmei.previousProcedureMethodExecutionInstances,
      flowchartData,
      pmeiIdToUnitOperationFields,
      buildOriginIndex
    ),
  };
}

type LinkedPMEIInstances = PMEINode['nextProcedureMethodExecutionInstances'];

type BuildMaterialLinkIndex = typeof buildDownstreamIndex;

function mapLinkedPMEIs(
  rawInstances: LinkedPMEIInstances,
  pmeiIdToUnitOperationFields: Map<string, UnitOperationFields>
): Array<LinkedPMEI> {
  return accessibleObjs(rawInstances).flatMap((p) => {
    const fields = pmeiIdToUnitOperationFields.get(p.id);
    if (fields == null) {
      return [];
    }
    return [
      {
        id: fields.pmeiId,
        templateMethodVersionId: fields.templateMethodId,
        methodName: fields.methodName,
        conditionName: fields.conditionName,
        replicateNumber: fields.replicateNumber,
      },
    ];
  });
}

function extractMaterialLinkIndex(
  rawInstances: LinkedPMEIInstances,
  flowchartData: FlowchartVersionData | null,
  pmeiIdToUnitOperationFields: Map<string, UnitOperationFields>,
  build: BuildMaterialLinkIndex
): Map<string, Array<LinkedPMEI>> | null {
  if (flowchartData == null) {
    return null;
  }

  const linkedPMEIs = mapLinkedPMEIs(rawInstances, pmeiIdToUnitOperationFields);
  return build(flowchartData.edges, flowchartData.nodes, linkedPMEIs);
}

export default function usePMEISidepanelDetail(
  pmeiId: string,
  flowchartData: FlowchartVersionData | null,
  pmeiIdToUnitOperationFields: Map<string, UnitOperationFields>
): {
  loading: boolean;
  data: PMEISidepanelDetailData | null;
} {
  const result = useBenchlingQuery<PMEISidepanelDetailQuery, PMEISidepanelDetailQueryVariables>(
    PMEI_SIDEPANEL_DETAIL_QUERY,
    {
      variables: {pmeiId},
    }
  );

  const pmei = result.type === QueryResultType.SUCCESS ? result.data.procedureMethodExecutionInstance : null;

  // Entity equipment is graded by comparing the linked entities' models, which live on the entity's
  // "Model" field rather than the equipment value itself, so fetch those entities to resolve them.
  const bioentityIds = useMemo(
    () => (pmei != null && !isInaccessible(pmei) ? [...new Set(equipmentEntityIds(pmei))] : []),
    [pmei]
  );
  const bioentities = useBioentitiesForProcedures(bioentityIds);
  const bioentitiesById = useMemo(
    () => new Map((bioentities ?? []).map((entity) => [entity.id, entity])),
    [bioentities]
  );

  const data = useMemo(
    () =>
      pmei == null
        ? null
        : buildPMEISidepanelDetailData(pmei, flowchartData, pmeiIdToUnitOperationFields, bioentitiesById),
    [pmei, flowchartData, pmeiIdToUnitOperationFields, bioentitiesById]
  );

  if (result.type === QueryResultType.PENDING) {
    return {loading: true, data: null};
  }

  if (result.type === QueryResultType.ERROR) {
    return {loading: false, data: null};
  }

  return {loading: false, data};
}
